import React, { useEffect, useRef, useState } from 'react'
import moment from 'moment'
import { AlertCircle } from 'lucide-react'
import { cn } from 'lib/utils'
import MessageAttachments from './MessageAttachments'
import MessageActions from 'features/Chat/shared/MessageActions'
import MessageReactions, { myReactionOf } from 'features/Chat/shared/MessageReactions'
import type { IChatMessage } from 'features/Chat/Chat.types'

export type TRunPosition = 'single' | 'first' | 'middle' | 'last'

export interface Props {
	message: IChatMessage
	isOutgoing: boolean
	position: TRunPosition
	/** Id of the signed-in user, for working out which reaction is theirs. */
	userId: string
	/** True for the one message the user is allowed to edit: their latest. */
	isEditable?: boolean
	/** Retries a send that failed. Only wired for outgoing bubbles. */
	onRetry?: (clientId: string) => void
	/** Saves new text for this message. */
	onEdit?: (messageId: string, text: string) => Promise<void>
	/** Sets, changes or clears the current user's reaction. */
	onReact?: (messageId: string, emoji: string) => void
}

/**
 * Corner radii: the corner facing the neighbouring bubble in a run tightens,
 * which is what makes a run read as one block rather than three balloons.
 */
const RADIUS: Record<TRunPosition, { out: string; in: string }> = {
	single: { out: '', in: '' },
	first: { out: 'rounded-br-[6px]', in: 'rounded-bl-[6px]' },
	middle: { out: 'rounded-r-[6px]', in: 'rounded-l-[6px]' },
	last: { out: 'rounded-tr-[6px]', in: 'rounded-tl-[6px]' },
}

/**
 * The tail on the last bubble of a run, as an inline SVG.
 *
 * A rotated square is the obvious approach and the wrong one: it bleeds
 * through the rounded corner it sits against and it lands inside the text
 * selection. This is a path, positioned outside the bubble box and overlapping
 * it by 4px so there is no seam where the two meet.
 *
 * The two paths are written out rather than one path mirrored with a
 * transform: `scale-x-[-1]` flips about the element's own centre, which fought
 * the absolute offset and left the incoming tail as a sliver in the wrong
 * place.
 */
const TAIL_OUT = 'M0 4 C0 14 4 20 12 20 C7 20 2 18 0 14 Z'
const TAIL_IN = 'M12 4 C12 14 8 20 0 20 C5 20 10 18 12 14 Z'

const Tail = ({ isOutgoing }: { isOutgoing: boolean }) => (
	<svg
		viewBox="0 0 12 20"
		aria-hidden="true"
		className={cn(
			'pointer-events-none absolute bottom-0 h-5 w-3',
			isOutgoing ? '-right-2 fill-[#0080fe]' : '-left-2 fill-[#E9E9EB]',
		)}
	>
		<path d={isOutgoing ? TAIL_OUT : TAIL_IN} />
	</svg>
)

const MessageBubble = ({
	message,
	isOutgoing,
	position,
	userId,
	isEditable = false,
	onRetry,
	onEdit,
	onReact,
}: Props) => {
	const [showActions, setShowActions] = useState(false)
	const [isEditing, setIsEditing] = useState(false)
	const [draft, setDraft] = useState(message.message || '')
	const [isSaving, setIsSaving] = useState(false)
	const editRef = useRef<HTMLTextAreaElement>(null)

	const hasTail = position === 'single' || position === 'last'
	const failed = message.deliveryStatus === 'failed'
	const sending = message.deliveryStatus === 'sending'
	const hasBody = !!message.message
	const reactions = message.reactions || []

	useEffect(() => {
		if (!isEditing) return
		setDraft(message.message || '')
		// Focus at the end, so the caret is where someone fixing a typo expects.
		requestAnimationFrame(() => {
			const el = editRef.current
			if (!el) return
			el.focus()
			el.setSelectionRange(el.value.length, el.value.length)
		})
		// eslint-disable-next-line
	}, [isEditing])

	const save = async () => {
		const text = draft.trim()
		if (!text || text === message.message) {
			setIsEditing(false)
			return
		}
		setIsSaving(true)
		try {
			await onEdit?.(message._id, text)
			setIsEditing(false)
		} catch {
			// The server refused, most likely because a newer message landed.
			// Leave the editor open with the text intact rather than discarding it.
		} finally {
			setIsSaving(false)
		}
	}

	const onEditKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (e.key === 'Escape') {
			e.preventDefault()
			setIsEditing(false)
			return
		}
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault()
			save()
		}
	}

	const timestamp = (
		<span
			className={cn(
				'shrink-0 text-[11px] text-gray-400 transition-opacity',
				showActions ? 'opacity-100' : 'opacity-0',
			)}
		>
			{moment(message.createdAt).format('LT')}
		</span>
	)

	/** The hover controls: react, and edit when this is the user's latest. */
	const actions = (
		<MessageActions
			messageId={message._id}
			align={isOutgoing ? 'right' : 'left'}
			isVisible={showActions}
			canEdit={isEditable && !!onEdit && !failed}
			myReaction={myReactionOf(reactions, userId)}
			onReact={onReact}
			onStartEdit={() => setIsEditing(true)}
		/>
	)

	return (
		<div
			data-testid="chat-message"
			data-direction={isOutgoing ? 'out' : 'in'}
			data-message-id={message._id}
			className={cn('flex items-end gap-1.5', isOutgoing ? 'justify-end' : 'justify-start')}
			onMouseEnter={() => setShowActions(true)}
			onMouseLeave={() => setShowActions(false)}
		>
			{/* Exact time and the hover controls, on the side away from the
			    bubble. Ordered so both sides mirror. */}
			{isOutgoing && (
				<>
					{timestamp}
					{actions}
				</>
			)}

			<div className="relative">
				<div
					className={cn(
						'relative max-w-full rounded-[18px] px-3.5 py-2 text-[15px] leading-[1.3]',
						'whitespace-pre-wrap break-words',
						isOutgoing
							? cn('bg-[#0080fe] text-white', RADIUS[position].out)
							: cn('bg-[#E9E9EB] text-black', RADIUS[position].in),
						sending && 'opacity-60',
						failed && 'bg-red-100 text-red-900',
						// Media-only messages get no balloon: the image is the bubble.
						!hasBody && !isEditing && 'bg-transparent p-0',
						// Room for the reaction chip to overlap the bottom edge.
						reactions.length > 0 && 'mb-2',
					)}
				>
					{isEditing ? (
						<div className="flex flex-col gap-1">
							<textarea
								ref={editRef}
								value={draft}
								disabled={isSaving}
								onChange={e => setDraft(e.target.value)}
								onKeyDown={onEditKeyDown}
								rows={Math.min(5, (draft.match(/\n/g)?.length ?? 0) + 1)}
								aria-label="Edit message"
								className={cn(
									'w-[min(220px,60vw)] resize-none bg-transparent text-[15px] leading-[1.3] outline-none',
									isOutgoing ? 'text-white placeholder:text-white/60' : 'text-black',
								)}
							/>
							<span
								className={cn(
									'text-[10px]',
									isOutgoing ? 'text-white/70' : 'text-gray-500',
								)}
							>
								Enter to save · Esc to cancel
							</span>
						</div>
					) : (
						hasBody && message.message
					)}

					<MessageAttachments attachments={message.attachments} className={hasBody ? '' : 'mt-0'} />
					{hasTail && hasBody && !failed && !isEditing && <Tail isOutgoing={isOutgoing} />}
				</div>

				<MessageReactions
					className={cn('absolute -bottom-2', isOutgoing ? 'left-0' : 'right-0')}
					reactions={reactions}
					userId={userId}
					onToggle={emoji => onReact?.(message._id, emoji)}
				/>
			</div>

			{!isOutgoing && (
				<>
					{actions}
					{timestamp}
				</>
			)}

			{message.editedAt && !isEditing && (
				<span className="shrink-0 self-end text-[10px] italic text-gray-400">Edited</span>
			)}

			{failed && message.clientId && (
				<button
					type="button"
					onClick={() => onRetry?.(message.clientId as string)}
					className="inline-flex shrink-0 items-center gap-1 text-[11px] font-medium text-red-600 hover:underline"
				>
					<AlertCircle className="h-3 w-3" />
					Retry
				</button>
			)}
		</div>
	)
}

export default MessageBubble
